"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { VoiceRecorderProps } from "./types";

interface RecognitionAlternative {
  transcript: string;
  confidence?: number;
}

interface RecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): RecognitionAlternative;
  [index: number]: RecognitionAlternative;
}

interface RecognitionResultList {
  length: number;
  item(index: number): RecognitionResult;
  [index: number]: RecognitionResult;
}

interface RecognitionEvent extends Event {
  results: RecognitionResultList;
  resultIndex: number;
}

interface RecognitionErrorEvent extends Event {
  error?: string;
  message?: string;
}

interface RecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort?: () => void;
  onstart: (() => void) | null;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onspeechend: (() => void) | null;
}

type RecognitionConstructor = new () => RecognitionInstance;

type RecognitionWindow = Window & {
  SpeechRecognition?: RecognitionConstructor;
  webkitSpeechRecognition?: RecognitionConstructor;
};

const MAX_RECORDING_TIME = 60;
const SILENCE_TIMEOUT_S = 4; // 4秒无新识别结果则自动停止（fallback）
const FFT_SIZE = 256;
const RMS_SILENCE_THRESHOLD = 0.012;
const AUDIO_SILENCE_TIMEOUT_MS = 1800; // 1.8 秒无真实音频输入则自动停止

export default function VoiceRecorder({
  onTranscript,
  onInterim,
  isProcessing,
  status,
  onToggle,
  className,
  compact = false,
  layout = "vertical",
}: VoiceRecorderProps) {
  const chatT = useTranslations("chat");
  const businessT = useTranslations("businessCommon");

  const [isSupported, setIsSupported] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [qualityWarning, setQualityWarning] = useState<string | null>(null);
  const [volumeLevel, setVolumeLevel] = useState(0);

  // 实时波形高度用振幅驱动，音量越大柱子越高，并保留波动纹理
  const waveBars = useMemo(() => {
    const normalized = Math.max(0, Math.min(1, volumeLevel / 100));
    return Array.from({ length: 16 }, (_, index) => {
      const texture =
        0.25 + 0.75 * Math.abs(Math.sin(index * 0.6 + normalized * Math.PI));
      const height = 4 + normalized * texture * 32;
      return `${height}px`;
    });
  }, [volumeLevel]);

  // 水平布局使用的低高度波形，避免撑大容器
  const smallWaveBars = useMemo(() => {
    const normalized = Math.max(0, Math.min(1, volumeLevel / 100));
    return Array.from({ length: 16 }, (_, index) => {
      const texture =
        0.25 + 0.75 * Math.abs(Math.sin(index * 0.6 + normalized * Math.PI));
      const height = 3 + normalized * texture * 14;
      return `${height}px`;
    });
  }, [volumeLevel]);

  const recognitionRef = useRef<RecognitionInstance | null>(null);
  const timerRef = useRef<number | null>(null);
  const volumeIntervalRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const finalTranscriptRef = useRef("");
  const onInterimRef = useRef(onInterim);
  const onTranscriptRef = useRef(onTranscript);
  const isRecordingRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastSoundTimeRef = useRef<number>(0);

  // 把会变化的回调/工具函数放入 ref，使 recognition effect 不因此重跑
  const businessTRef = useRef(businessT);
  const chatTRef = useRef(chatT);
  const onToggleRef = useRef(onToggle);
  const clearIntervalsRef = useRef<(() => void) | null>(null);
  const resetSilenceTimerRef = useRef<((stopFn: () => void) => void) | null>(
    null,
  );
  const stopAudioRef = useRef<(() => void) | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);

  const recognitionConstructor = useMemo<RecognitionConstructor | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    const { SpeechRecognition, webkitSpeechRecognition } =
      window as RecognitionWindow;
    return SpeechRecognition ?? webkitSpeechRecognition ?? null;
  }, []);

  const clearIntervals = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (volumeIntervalRef.current !== null) {
      window.clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
    }

    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const resetSilenceTimer = useCallback((stopFn: () => void) => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
    }
    silenceTimerRef.current = window.setTimeout(() => {
      console.info("[VoiceRecorder] Silence timeout, auto-stopping");
      stopFn();
    }, SILENCE_TIMEOUT_S * 1000);
  }, []);

  const stopAudio = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        // ignore
      }
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch {
        // ignore
      }
      analyserRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        void audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    stopAudio();
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch (err) {
        console.error("Failed to stop recognition:", err);
      }
    }

    setIsRecording(false);
    onToggle(false);
    clearIntervals();
  }, [clearIntervals, onToggle, stopAudio]);

  // 把回调/工具函数同步到 refs，避免 recognition effect 因它们重建而反复创建实例
  useEffect(() => {
    onInterimRef.current = onInterim;
    onTranscriptRef.current = onTranscript;
    businessTRef.current = businessT;
    chatTRef.current = chatT;
    onToggleRef.current = onToggle;
    clearIntervalsRef.current = clearIntervals;
    resetSilenceTimerRef.current = resetSilenceTimer;
    stopAudioRef.current = stopAudio;
    stopRecordingRef.current = stopRecording;
  });

  const startRecording = useCallback(() => {
    // 防连点：isRecordingRef 同步更新，快速重复点击时第二次直接忽略
    if (isProcessing || isRecordingRef.current) {
      return;
    }

    if (!recognitionRef.current) {
      setIsSupported(false);
      setError(chatT("your_browser_does_not_support_speech_synthesis"));
      return;
    }

    const startVolumeFallback = () => {
      if (volumeIntervalRef.current !== null || !isRecordingRef.current) return;
      volumeIntervalRef.current = window.setInterval(() => {
        const randomVolume = Math.floor(Math.random() * 100);
        setVolumeLevel(randomVolume);

        if (randomVolume < 20) {
          setQualityWarning(businessT("volume_too_low"));
        } else if (randomVolume > 90) {
          setQualityWarning(businessT("volume_too_high"));
        } else {
          setQualityWarning(null);
        }
      }, 300);
    };

    const processAudio = () => {
      if (!isRecordingRef.current || !analyserRef.current) return;

      const analyser = analyserRef.current;
      const bufferLength = analyser.fftSize;
      const timeData = new Uint8Array(bufferLength);
      analyser.getByteTimeDomainData(timeData);

      let sumSq = 0;
      for (let i = 0; i < bufferLength; i += 1) {
        const x = (timeData[i] - 128) / 128;
        sumSq += x * x;
      }
      const rms = Math.sqrt(sumSq / bufferLength);
      const normalized = Math.max(0, Math.min(1, rms * 8));
      setVolumeLevel(Math.round(normalized * 100));

      if (rms > RMS_SILENCE_THRESHOLD) {
        lastSoundTimeRef.current = Date.now();
      }
      if (Date.now() - lastSoundTimeRef.current > AUDIO_SILENCE_TIMEOUT_MS) {
        console.info("[VoiceRecorder] Audio silence timeout, auto-stopping");
        stopRecording();
        return;
      }

      if (normalized < 0.08) {
        setQualityWarning(businessT("volume_too_low"));
      } else if (normalized > 0.95) {
        setQualityWarning(businessT("volume_too_high"));
      } else {
        setQualityWarning(null);
      }

      animationFrameRef.current = requestAnimationFrame(processAudio);
    };

    const startAudio = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("getUserMedia not supported");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // 用户在拿到权限前已经停止，直接释放流
        if (!isRecordingRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        const AudioContextClass =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          throw new Error("AudioContext not supported");
        }

        const audioContext: AudioContext = new AudioContextClass();
        audioContextRef.current = audioContext;
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        // resume 期间用户停止，关闭已创建的 AudioContext
        if (!isRecordingRef.current) {
          stopAudio();
          return;
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        analyser.smoothingTimeConstant = 0.7;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        sourceRef.current = source;

        lastSoundTimeRef.current = Date.now();
        processAudio();
      } catch (err) {
        console.warn(
          "[VoiceRecorder] Audio stream not available, using fallback",
          err,
        );
        startVolumeFallback();
      }
    };

    try {
      finalTranscriptRef.current = "";
      setTranscript("");
      isRecordingRef.current = true;
      setRecordingTime(0);
      setQualityWarning(null);
      setError(null);
      setVolumeLevel(0);

      clearIntervals();

      // 先启动语音识别（用户预期的核心行为），再在后台初始化真实音频波形分析
      try {
        recognitionRef.current.start();
      } catch (err) {
        // stop() 是异步的，onend 未触发前再次 start() 会抛 InvalidStateError；
        // 快速连点场景下静默忽略，由 onend 回调自行收敛状态
        if (err instanceof DOMException && err.name === "InvalidStateError") {
          console.warn("[VoiceRecorder] recognition already started, ignored");
          isRecordingRef.current = false;
          setIsRecording(false);
          onToggle(false);
          return;
        }
        throw err;
      }
      setIsRecording(true);

      // 在后台初始化真实音频分析；不阻塞 SpeechRecognition 启动
      void startAudio();
      onToggle(true);

      // 静音超时：启动后 4 秒无新识别结果则自动停止（fallback）
      resetSilenceTimer(stopRecording);

      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            setError(businessT("recording_too_long"));
            return prev;
          }

          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Failed to start recognition:", err);
      setError(businessT("voice_processing_failed"));
      isRecordingRef.current = false;
      setIsRecording(false);
      onToggle(false);
      clearIntervals();
      stopAudio();
    }
  }, [
    businessT,
    chatT,
    clearIntervals,
    isProcessing,
    onToggle,
    resetSilenceTimer,
    stopAudio,
    stopRecording,
  ]);

  useEffect(() => {
    if (!recognitionConstructor) {
      setIsSupported(false);
      setError(
        chatTRef.current("your_browser_does_not_support_speech_synthesis"),
      );
      return;
    }

    const recognition = new recognitionConstructor();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "zh-CN";

    recognition.onstart = () => {
      setError(null);
      setTranscript("");
      finalTranscriptRef.current = "";
    };

    recognition.onresult = (event: RecognitionEvent) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results.item(i);
        if (!result) continue;

        const alternative = result.item(0);
        if (!alternative) continue;

        interimTranscript += alternative.transcript;
        if (result.isFinal) {
          finalTranscript += alternative.transcript;
        }
      }

      finalTranscriptRef.current = finalTranscript;
      setTranscript(interimTranscript);
      onInterimRef.current?.(interimTranscript);

      if (interimTranscript.trim()) {
        // 只要有识别结果（interim/final）就重置静音超时
        resetSilenceTimerRef.current?.(stopRecordingRef.current ?? (() => {}));
      }
    };

    recognition.onerror = (event: RecognitionErrorEvent) => {
      const detailRaw = event?.error ?? event?.message ?? "";
      const detail = detailRaw.toString().trim();
      const normalized = detail.toLowerCase();
      const businessTrans = businessTRef.current;

      if (normalized === "aborted") {
        console.info("Speech recognition aborted by the client.");
        return;
      }

      if (normalized === "no-speech") {
        console.info("Speech recognition reported no speech.");
        setError(businessTrans("voice_input_processing_error_no_speech"));
        stopRecordingRef.current?.();
        return;
      }

      if (normalized === "network") {
        console.warn("Speech recognition network error:", event);
        setError(businessTrans("voice_input_processing_error_network"));
        stopRecordingRef.current?.();
        return;
      }

      if (detail) {
        console.error("Speech recognition error:", detail, event);
        setError(`${businessTrans("voice_input_processing_error")} ${detail}`);
      } else {
        console.warn("Speech recognition error without detail:", event);
        setError(businessTrans("voice_processing_failed"));
      }

      stopRecordingRef.current?.();
    };

    recognition.onend = () => {
      const finalText = finalTranscriptRef.current.trim();
      finalTranscriptRef.current = "";
      setIsRecording(false);
      isRecordingRef.current = false;
      stopAudioRef.current?.();
      onToggleRef.current(false);
      clearIntervalsRef.current?.();
      if (finalText) {
        void onTranscriptRef.current(finalText);
      }
    };

    recognition.onspeechend = () => {
      if (isRecordingRef.current) {
        stopRecordingRef.current?.();
      }
    };

    return () => {
      const current = recognitionRef.current;

      if (current) {
        try {
          current.stop();
        } catch (err) {
          console.error("Failed to stop recognition on unmount:", err);
        }

        try {
          current.abort?.();
        } catch (err) {
          console.error("Failed to abort recognition on unmount:", err);
        }
      }

      stopAudioRef.current?.();
      clearIntervalsRef.current?.();

      if (recognitionRef.current === current) {
        recognitionRef.current = null;
      }
    };
  }, [recognitionConstructor]);

  const micTooltip = !isSupported
    ? chatT("your_browser_does_not_support_speech_synthesis")
    : isRecording
      ? chatT("stop_speech")
      : businessT("click_mic_to_record");

  const micButton = (
    <button
      type="button"
      onClick={isRecording ? stopRecording : startRecording}
      disabled={!isSupported || isProcessing}
      aria-label={micTooltip}
      className={cn(
        "shrink-0 relative z-10 flex h-14 w-14 items-center justify-center rounded-full transition-all duration-300",
        isRecording
          ? "bg-[radial-gradient(circle_at_30%_30%,hsl(var(--practice-voice-peach-strong)),hsl(var(--primary)))] text-white shadow-[0_0_25px_rgba(248,173,144,0.55)] scale-105"
          : "bg-[radial-gradient(circle_at_30%_30%,hsl(var(--practice-voice-peach)),hsl(var(--practice-voice-peach-strong)))] text-[hsl(var(--practice-voice-text))] shadow-lg hover:shadow-xl",
        !isSupported || isProcessing
          ? "opacity-50 cursor-not-allowed"
          : "hover:-translate-y-0.5",
        className,
      )}
    >
      <span
        className={`absolute inset-0 rounded-full blur-lg transition-opacity duration-300 ${
          isRecording
            ? "bg-[radial-gradient(circle_at_30%_30%,hsla(var(--practice-voice-peach-strong)/0.6),hsla(var(--primary)/0.45))] opacity-80"
            : "opacity-0"
        }`}
        aria-hidden="true"
      />
      {isRecording ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="relative h-6 w-6"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <rect x="7" y="7" width="10" height="10" rx="2" ry="2" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="relative h-6 w-6"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 16a3 3 0 003-3V7a3 3 0 10-6 0v6a3 3 0 003 3z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 12a5 5 0 0010 0M12 19v2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );

  if (!isSupported) {
    return compact ? (
      <SimpleTooltip
        content={micTooltip}
        side="top"
        className="bg-white text-slate-800 border border-slate-200 dark:bg-slate-800 dark:text-slate-100"
      >
        {micButton}
      </SimpleTooltip>
    ) : (
      <div className="text-sm text-muted-foreground">
        <p>{chatT("your_browser_does_not_support_speech_synthesis")}</p>
      </div>
    );
  }

  const recordingPanel = isRecording ? (
    <div className="w-full space-y-2 rounded-2xl border border-rose-100 bg-white/80 p-3 shadow-inner">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{chatT("recording_started")}</span>
        <span>
          {chatT("recording_duration")}: {recordingTime}s
        </span>
      </div>
      <div
        className="min-h-[1.25rem] text-xs text-slate-600 truncate"
        title={transcript}
      >
        {transcript || (
          <span className="text-slate-400">{chatT("listening_status")}</span>
        )}
      </div>
      <div className="flex h-10 items-end gap-1 overflow-hidden">
        {waveBars.map((height, index) => (
          <div
            key={index}
            className="w-1.5 rounded-full bg-gradient-to-t from-rose-300 via-orange-300 to-sky-400 transition-[height] duration-200 ease-out"
            style={{ height }}
          />
        ))}
      </div>
      {qualityWarning && (
        <div className="text-xs text-amber-500">{qualityWarning}</div>
      )}
    </div>
  ) : null;

  if (compact) {
    return (
      <>
        <SimpleTooltip
          content={micTooltip}
          side="top"
          className="bg-white text-slate-800 border border-slate-200 dark:bg-slate-800 dark:text-slate-100"
        >
          {micButton}
        </SimpleTooltip>

        {isRecording && (
          <div
            data-testid="voice-recording-panel"
            className="absolute inset-0 -z-10 flex flex-col justify-center gap-1 rounded-2xl border border-rose-100 bg-white/90 pl-16 pr-4 shadow-inner dark:border-slate-700 dark:bg-slate-900/80"
          >
            <div className="flex min-w-0 items-center justify-between text-[10px] text-slate-500">
              <span className="min-w-0 flex-1 truncate">
                {transcript || chatT("recording_started")}
              </span>
              <span className="shrink-0 ml-2">{recordingTime}s</span>
            </div>
            <div className="flex h-5 flex-1 items-end gap-1 overflow-hidden">
              {waveBars.map((height, index) => (
                <div
                  key={index}
                  className="w-1 rounded-full bg-gradient-to-t from-rose-300 via-orange-300 to-sky-400 transition-[height] duration-200 ease-out"
                  style={{ height }}
                />
              ))}
            </div>
            {qualityWarning && (
              <span className="shrink-0 text-[10px] text-amber-500">
                {qualityWarning}
              </span>
            )}
          </div>
        )}

        {error && <p className="sr-only">{error}</p>}

        {!isRecording && transcript && (
          <div className="sr-only" title={transcript}>
            {transcript}
          </div>
        )}
      </>
    );
  }

  if (layout === "horizontal") {
    return (
      <div
        data-testid="voice-recording-panel"
        className="flex w-full items-center gap-3"
      >
        <div className="shrink-0">{micButton}</div>
        <div className="min-w-0 flex-1">
          {isRecording ? (
            <div className="flex h-9 items-center gap-2 rounded-md border border-rose-100 bg-white/80 px-2 shadow-inner">
              <span
                className="min-w-0 flex-1 truncate text-xs text-slate-600"
                title={transcript}
              >
                {transcript || (
                  <span className="text-slate-400">
                    {chatT("listening_status")}
                  </span>
                )}
              </span>
              <div className="flex h-5 w-20 shrink-0 items-end gap-0.5 overflow-hidden">
                {smallWaveBars.map((height, index) => (
                  <div
                    key={index}
                    className="w-1 rounded-full bg-gradient-to-t from-rose-300 via-orange-300 to-sky-400 transition-[height] duration-200 ease-out"
                    style={{ height }}
                  />
                ))}
              </div>
              <span className="shrink-0 text-xs text-slate-500">
                {recordingTime}s
              </span>
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              {businessT("click_mic_to_record")}
            </div>
          )}
          {status && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
              {isProcessing && (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              )}
              {status}
            </div>
          )}
          {error && <p className="mt-1 text-sm text-rose-500">{error}</p>}
          {isRecording && qualityWarning && (
            <div className="mt-1 text-xs text-amber-500">{qualityWarning}</div>
          )}
          {!isRecording && transcript && (
            <div
              className="mt-1 text-xs text-slate-500 truncate"
              title={transcript}
            >
              {transcript}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="voice-recording-panel"
      className="flex flex-col items-start gap-3"
    >
      {micButton}

      <div className="text-xs text-slate-500">
        {isRecording
          ? `🎙 ${recordingTime}s — ${businessT("click_mic_to_record")}`
          : businessT("click_mic_to_record")}
      </div>

      {status && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          {isProcessing && (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          )}
          {status}
        </div>
      )}

      {error && <p className="text-sm text-rose-500">{error}</p>}

      {recordingPanel}

      {!isRecording && transcript && (
        <div className="text-xs text-slate-500 truncate" title={transcript}>
          {transcript}
        </div>
      )}
    </div>
  );
}
